import { isHotkey, isTextEntryTarget } from '@tunasong/models'
import { useEffect } from 'react'

export type HotkeyHandler = (ev?: KeyboardEvent) => void

/**
 * Register Hotkeys and handlers for the hotkeys. The listeners are registered on the document. To disable, set the second argument
 *
 * GH #1806: keydown from a focused `<input>`/`<textarea>`/`<select>` is ignored
 * by default. The listener defaults to `document.body`, so every keystroke
 * typed anywhere in the app reaches it — without this, a plain-character
 * binding like `Space` is `preventDefault()`ed inside every text field in the
 * app while the registering component is mounted. Set `allowInInput` for keys
 * that are meant to work from within a control (`Escape` to dismiss, etc.).
 */
export const useHotkey = ({
  hotkey,
  handler,
  disabled = false,
  allowInInput = false,
  el,
}: {
  hotkey: string
  handler?: HotkeyHandler
  disabled?: boolean
  /** Fire even when the keydown came from a text input. Default false. */
  allowInInput?: boolean
  el?: HTMLElement | null
}) => {
  const element = el ? el : document.body
  useEffect(() => {
    if (disabled) {
      return
    }
    const handleKey = (ev: KeyboardEvent) => {
      if (!allowInInput && isTextEntryTarget(ev.target)) {
        return
      }
      if (isHotkey(hotkey)(ev) && handler) {
        ev.preventDefault()
        handler(ev)
      }
    }
    element.addEventListener('keydown', handleKey)
    return () => {
      element.removeEventListener('keydown', handleKey)
    }
  }, [allowInInput, disabled, handler, hotkey, element])
}
